import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { LineComputed, VatCategory } from '@financiar/shared';
import { UNIT_CODES, unitLabelForCode } from '@financiar/shared';
import { Button, Form, Input, Select, Table, Typography } from 'antd';
import type { FormInstance, FormListFieldData } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Product } from '../api/types';
import { MoneyInput, MoneyText, QtyInput } from '../lib/format';

const { Text } = Typography;

const UNIT_OPTIONS = UNIT_CODES.map((unit) => ({ value: unit.code, label: unit.label }));

export interface VatCategoryOption {
  value: VatCategory;
  label: string;
  disabled?: boolean;
}

/** The shape `addLine()` seeds a new row with. Exported so a page can build
 * the form's `initialValues` with the same defaults the "+ Adaugă linie"
 * button uses. */
export function emptyLine(): { unitCode: string; vatCategory: VatCategory } {
  return { unitCode: 'H87', vatCategory: 'STANDARD' };
}

export interface LineItemsEditorProps {
  form: FormInstance;
  /** The `Form.List` path — `'lines'` for an invoice draft, `'items'` for a
   * subscription, which is why nothing here is invoice-specific. */
  listName: string;
  products: Product[];
  isLoadingProducts?: boolean;
  vatCategoryOptions: VatCategoryOption[];
  /** False for a firm that is not a VAT payer today: the Cotă TVA and TVA
   * columns disappear entirely (the underlying `vatCategory` value stays in
   * the form store — the API requires it on every line regardless). */
  showVat: boolean;
  /** Per-line `lineNet`/`lineVat`, index-aligned with the list, from the
   * shared `computeInvoiceTotals`. `null` while the totals can't be computed
   * (no applicable VAT rate), which renders the value cells as "—". */
  computedLines: LineComputed[] | null;
  currency?: string;
  disabled?: boolean;
}

/**
 * The editable invoice/subscription line table — SHARED between the invoice
 * draft editor (`features/facturi/FacturaEditorPage.tsx`, `listName="lines"`)
 * and the subscription editor (`features/abonamente`, `listName="items"`):
 * an AntD `Table` whose rows are `Form.List` fields, so every cell is a real
 * `Form.Item` and a server error reported at `lines[0].quantity` (or
 * `items[0].quantity`) lands on the exact input that produced it (see
 * `applyServerErrors`). One source of truth for both editors — see
 * task-8d-brief.md's closing constraint.
 *
 * A product is OPTIONAL on a line. Picking one autocompletes denumire / UM /
 * preț / categorie TVA and then gets out of the way — every autocompleted
 * field stays editable, and a line with no product at all (pure free text) is
 * perfectly valid.
 */
export function LineItemsEditor({
  form,
  listName,
  products,
  isLoadingProducts,
  vatCategoryOptions,
  showVat,
  computedLines,
  currency = 'RON',
  disabled,
}: LineItemsEditorProps) {
  const productOptions = products.map((product) => ({ value: product.id, label: product.name }));

  function applyProduct(index: number, productId: string | undefined) {
    if (!productId) return;
    const product = products.find((candidate) => candidate.id === productId);
    if (!product) return;
    form.setFieldValue([listName, index, 'description'], product.name);
    form.setFieldValue([listName, index, 'unitCode'], product.unitCode);
    form.setFieldValue([listName, index, 'unitPrice'], product.unitPrice);
    form.setFieldValue([listName, index, 'vatCategory'], product.vatCategory);
  }

  return (
    <Form.List name={listName}>
      {(fields, { add, remove }) => {
        const columns: ColumnsType<FormListFieldData> = [
          {
            title: '#',
            key: 'position',
            width: 44,
            render: (_, __, index) => <Text type="secondary">{index + 1}</Text>,
          },
          {
            title: 'Produs',
            key: 'productId',
            width: 190,
            render: (_, field) => (
              <Form.Item name={[field.name, 'productId']} style={{ margin: 0 }}>
                <Select
                  showSearch
                  allowClear
                  placeholder="Opțional"
                  optionFilterProp="label"
                  options={productOptions}
                  loading={isLoadingProducts}
                  onChange={(value: string | undefined) => applyProduct(field.name, value)}
                />
              </Form.Item>
            ),
          },
          {
            title: 'Denumire',
            key: 'description',
            width: 260,
            render: (_, field) => (
              <Form.Item
                name={[field.name, 'description']}
                style={{ margin: 0 }}
                rules={[{ required: true, message: 'Denumirea este obligatorie.' }]}
              >
                <Input placeholder="Denumirea produsului sau serviciului" />
              </Form.Item>
            ),
          },
          {
            title: 'UM',
            key: 'unitCode',
            width: 110,
            render: (_, field) => (
              <Form.Item name={[field.name, 'unitCode']} style={{ margin: 0 }} rules={[{ required: true, message: 'UM' }]}>
                <Select options={UNIT_OPTIONS} />
              </Form.Item>
            ),
          },
          {
            title: 'Cant.',
            key: 'quantity',
            width: 110,
            render: (_, field) => (
              // No client-side "must not be zero" rule on purpose: that rule
              // lives in the server's zod schema, and letting the request
              // through is what puts the server's own message on this exact
              // field via applyServerErrors.
              <Form.Item
                name={[field.name, 'quantity']}
                style={{ margin: 0 }}
                rules={[{ required: true, message: 'Cantitatea este obligatorie.' }]}
              >
                <QtyInput />
              </Form.Item>
            ),
          },
          {
            title: 'Preț unitar',
            key: 'unitPrice',
            width: 130,
            render: (_, field) => (
              <Form.Item
                name={[field.name, 'unitPrice']}
                style={{ margin: 0 }}
                rules={[{ required: true, message: 'Prețul este obligatoriu.' }]}
              >
                <MoneyInput />
              </Form.Item>
            ),
          },
          ...(showVat
            ? ([
                {
                  title: 'Cotă TVA',
                  key: 'vatCategory',
                  width: 160,
                  render: (_, field) => (
                    <Form.Item
                      name={[field.name, 'vatCategory']}
                      style={{ margin: 0 }}
                      rules={[{ required: true, message: 'Cota TVA este obligatorie.' }]}
                    >
                      <Select options={vatCategoryOptions} />
                    </Form.Item>
                  ),
                },
              ] as ColumnsType<FormListFieldData>)
            : []),
          {
            title: 'Valoare',
            key: 'lineNet',
            width: 120,
            align: 'right',
            render: (_, __, index) => {
              const computed = computedLines?.[index];
              return computed ? <MoneyText value={computed.lineNet} currency={currency} /> : <Text type="secondary">—</Text>;
            },
          },
          ...(showVat
            ? ([
                {
                  title: 'TVA',
                  key: 'lineVat',
                  width: 120,
                  align: 'right',
                  render: (_, __, index) => {
                    const computed = computedLines?.[index];
                    return computed ? (
                      <MoneyText value={computed.lineVat} currency={currency} />
                    ) : (
                      <Text type="secondary">—</Text>
                    );
                  },
                },
              ] as ColumnsType<FormListFieldData>)
            : []),
          {
            title: '',
            key: 'remove',
            width: 48,
            render: (_, field) => (
              <Button
                type="text"
                danger
                aria-label="Șterge linia"
                icon={<DeleteOutlined />}
                // A draft must keep at least one line (the API's schema
                // requires >= 1), so the last row can't be removed.
                disabled={disabled || fields.length <= 1}
                onClick={() => remove(field.name)}
              />
            ),
          },
        ];

        return (
          <>
            <Table<FormListFieldData>
              rowKey="key"
              size="small"
              pagination={false}
              dataSource={fields}
              columns={columns}
              scroll={{ x: showVat ? 1160 : 900 }}
            />
            <Button
              type="dashed"
              block
              icon={<PlusOutlined />}
              disabled={disabled}
              style={{ marginTop: 12 }}
              onClick={() => add(emptyLine())}
            >
              Adaugă linie
            </Button>
          </>
        );
      }}
    </Form.List>
  );
}

/** Maps the form's line rows onto the API's `invoiceLineInputSchema` /
 * `subscriptionItemInputSchema` shape — identical field sets, so one mapper
 * serves both callers. `unitLabel` is derived from the chosen code rather
 * than kept as a separate field — the two must agree, and UNIT_CODES is the
 * single source for both sides. */
export function toLinePayload(line: {
  productId?: string;
  description?: string;
  quantity?: string | null;
  unitCode?: string;
  unitPrice?: string | null;
  vatCategory?: VatCategory;
}) {
  const unitCode = line.unitCode ?? 'H87';
  return {
    productId: line.productId || undefined,
    description: (line.description ?? '').trim(),
    quantity: line.quantity ?? '',
    unitCode,
    unitLabel: unitLabelForCode(unitCode) ?? unitCode,
    unitPrice: line.unitPrice ?? '',
    vatCategory: line.vatCategory ?? 'STANDARD',
  };
}
